import { useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export function useErrorApiHandler(options = {}) {
  const { showToast = true, onCustomError } = options;
  const [serverErrors, setServerErrors] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [showToastState, setShowToastState] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const clearErrors = useCallback(() => {
    setServerErrors({});
  }, []);

  const clearFieldError = useCallback((fieldName) => {
    setServerErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const clearFieldErrors = useCallback((fieldNames) => {
    setServerErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      fieldNames.forEach((fieldName) => {
        delete newErrors[fieldName];
      });
      return newErrors;
    });
  }, []);

  const showErrorToast = useCallback(
    (message) => {
      if (showToast && typeof window !== 'undefined') {
        setToastMessage(message);
        setShowToastState(true);
      }
    },
    [showToast],
  );

  const handleToastClose = useCallback(() => {
    setShowToastState(false);
    setToastMessage('');
  }, []);

  const handleError = useCallback(
    (error) => {
      setServerErrors({});

      if (error?.isAxiosError || error?.response) {
        const status = error.response?.status;
        const errorData = error.response?.data;

        if (error?.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors[0]?.message) {
          showErrorToast(error.response.data.errors[0].message);
          return;
        }
        if (error?.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors[0]?.error) {
          showErrorToast(error.response.data.errors[0].error);
          return;
        }
        if (error?.response?.data?.errors && Array.isArray(error.response.data.errors.non_field_errors) && error.response.data.errors?.non_field_errors) {
          showErrorToast(error.response.data.errors.non_field_errors[0]);
          return;
        }

        if (errorData && typeof errorData === 'object') {
          if (!errorData.message && !errorData.error) {
            const fieldErrors = {};
            Object.keys(errorData).forEach((key) => {
              if (Array.isArray(errorData[key])) {
                fieldErrors[key] = errorData[key][0];
              } else if (typeof errorData[key] === 'string') {
                fieldErrors[key] = errorData[key];
              }
            });
            setServerErrors(fieldErrors);
            return;
          }

          if (errorData.errors && Array.isArray(errorData.errors)) {
            const fieldErrors = {};
            errorData.errors.forEach((err) => {
              const fieldName = err.field || err.param || err.path;
              if (fieldName) {
                fieldErrors[fieldName] = err.message || err.msg;
              }
            });
            setServerErrors(fieldErrors);
            return;
          }

          if (errorData.validationErrors && typeof errorData.validationErrors === 'object') {
            setServerErrors(errorData.validationErrors);
            return;
          }

          if (errorData.message || errorData.error) {
            showErrorToast(errorData.message || errorData.error);
            return;
          }
        }

        if (error?.response?.data?.errors?.non_field_errors && Array.isArray(error.response.data.errors.non_field_errors) && error.response.data.errors.non_field_errors[0]) {
          showErrorToast(error.response.data.errors.non_field_errors[0]);
          return;
        }

        if (status === 401) {
          return;
        }

        if (status === 403) {
          showErrorToast('شما اجازه دسترسی به این بخش را ندارید');
          return;
        }

        if (status === 404) {
          showErrorToast('منبع مورد نظر یافت نشد');
          return;
        }

        if (status && status >= 500) {
          showErrorToast('خطای سرور. لطفاً بعداً تلاش کنید');
          return;
        }
      }

      if (error?.message) {
        showErrorToast(error.message);
        return;
      }

      if (onCustomError) {
        onCustomError(error);
        return;
      }

      showErrorToast('خطای غیرمنتظره‌ای رخ داد');
    },
    [location, history, showToast, showErrorToast, onCustomError],
  );

  const getFieldError = useCallback(
    (fieldName) => {
      return serverErrors[fieldName];
    },
    [serverErrors],
  );

  const hasErrors = Object.keys(serverErrors).length > 0;

  return {
    serverErrors,
    clearErrors,
    clearFieldError,
    clearFieldErrors,
    handleError,
    hasErrors,
    getFieldError,
    // Toast related states and functions
    toastMessage,
    showToastState,
    handleToastClose,
  };
}
